import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type { ChangeRulesInput } from '../utils/adSettingsForm';
import type { UploadedCampaign } from '../utils/auctionInsightsForm';

/** One location's ad settings (backend ads/settings/adSettings.service.ts), shared by Ad Campaigns and Jenny. */
export interface AdSettings {
  /** The terms set for this location; empty means the defaults below are used. */
  brandTerms: string[];
  defaultBrandTerms: string[];
  /** The day the campaigns were rebuilt (YYYY-MM-DD): figures before it are not compared like-for-like. */
  baselineDate: string | null;
  /** The approved monthly ad budget: the campaign budgets the practice approved, unless the agency overrode it. */
  monthlyAdBudget: number | null;
  budgetSetBy: 'campaigns' | 'agency_override' | 'approved_change' | null;
  budgetSetAt: string | null;
  /** Fewest calls of 30s+ a campaign needs before Jenny recommends a change to it. */
  minCallsForChange: number;
  /** Days a campaign is left alone after any change before another is recommended. */
  changeCooldownDays: number;
  /** The practice's locations with their budgets and share of the total: the split recommendations stay inside. */
  locationBudgets: Array<{
    locationId: string;
    name: string;
    monthlyAdBudget: number | null;
    sharePercent: number | null;
  }>;
}

/** One domain's row in an uploaded auction insights export. */
export interface AuctionInsightRow {
  domain: string;
  isSelf: boolean;
  impressionSharePercent: number | null;
  overlapRatePercent: number | null;
  outrankingSharePercent: number | null;
  topOfPageRatePercent: number | null;
  /** Fields Google gave only as "< 10%". */
  underTen: string[];
}

export interface AuctionInsightUpload {
  campaign: string;
  matchedCampaign: string | null;
  periodStart: string;
  periodEnd: string;
  uploadedAt: string;
  rows: AuctionInsightRow[];
}

export interface AuctionInsightsUploadInput {
  periodStart?: string;
  periodEnd?: string;
  csv: string;
}

export interface AuctionInsightsUploadResult {
  period: { start: string; end: string };
  campaigns: UploadedCampaign[];
}

/** A change PracticeNova applied in Google Ads for an approved ads task (backend adsExecution.service.ts). */
export interface AppliedAdsChange {
  id: string;
  status: 'applying' | 'applied' | 'failed' | 'conflict' | 'undone';
  summary: string;
  reason: string | null;
  appliedAt: string | null;
  undo: { id: string; summary: string; appliedAt: string | null } | null;
}

export interface AdsChangeOutcome {
  appliedChangeId: string;
  status: AppliedAdsChange['status'];
  summary: string;
  reason: string | null;
}

const q = (locationId: string) => ({ params: { locationId } });

export const adSettingsApi = {
  get: async (locationId: string): Promise<AdSettings> => {
    const { data } = await apiClient.get<ApiResponse<AdSettings>>('/ads/settings', q(locationId));
    return data.data;
  },
  saveBrandTerms: async (locationId: string, brandTerms: string[]): Promise<AdSettings> => {
    const { data } = await apiClient.put<ApiResponse<AdSettings>>(
      '/ads/settings/brand-terms',
      { brandTerms },
      q(locationId),
    );
    return data.data;
  },
  /** Agency only. */
  saveChangeRules: async (locationId: string, input: ChangeRulesInput): Promise<AdSettings> => {
    const { data } = await apiClient.put<ApiResponse<AdSettings>>(
      '/ads/settings/change-rules',
      input,
      q(locationId),
    );
    return data.data;
  },
  /** Agency only. */
  overrideBudget: async (
    locationId: string,
    monthlyAdBudget: number | null,
  ): Promise<AdSettings> => {
    const { data } = await apiClient.put<ApiResponse<AdSettings>>(
      '/ads/settings/budget',
      { monthlyAdBudget },
      q(locationId),
    );
    return data.data;
  },
  appliedChange: async (locationId: string, taskId: string): Promise<AppliedAdsChange | null> => {
    const { data } = await apiClient.get<ApiResponse<AppliedAdsChange | null>>(
      '/ads/applied-changes',
      { params: { locationId, taskId } },
    );
    return data.data;
  },
  /** Owners and admins: set the change back to the values read before it (its own change, read back and logged). */
  undoAppliedChange: async (locationId: string, id: string): Promise<AdsChangeOutcome> => {
    const { data } = await apiClient.post<ApiResponse<AdsChangeOutcome>>(
      `/ads/applied-changes/${encodeURIComponent(id)}/undo`,
      {},
      q(locationId),
    );
    return data.data;
  },
  listAuctionInsights: async (locationId: string): Promise<AuctionInsightUpload[]> => {
    const { data } = await apiClient.get<ApiResponse<{ uploads: AuctionInsightUpload[] }>>(
      '/ads/auction-insights',
      q(locationId),
    );
    return data.data.uploads;
  },
  uploadAuctionInsights: async (
    locationId: string,
    input: AuctionInsightsUploadInput,
  ): Promise<AuctionInsightsUploadResult> => {
    const { data } = await apiClient.post<ApiResponse<AuctionInsightsUploadResult>>(
      '/ads/auction-insights',
      input,
      q(locationId),
    );
    return data.data;
  },
  removeAuctionInsights: async (
    locationId: string,
    input: { campaign: string; periodStart: string; periodEnd: string },
  ): Promise<{ deleted: number }> => {
    const { data } = await apiClient.delete<ApiResponse<{ deleted: number }>>(
      '/ads/auction-insights',
      { ...q(locationId), data: input },
    );
    return data.data;
  },
};
